import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PageType } from '../../types';
import { fetchWithAdminAuth } from '../../utils/adminAuth';

interface SidebarProps {
  activePage: PageType;
  isOpen: boolean;
  onClose: () => void;
}

const PAGE_TO_ROUTE: Record<PageType, string> = {
  'dashboard': '/backoffice',
  'consultores': '/backoffice/consultores',
  'clientes': '/backoffice/clientes',
  'sessoes': '/backoffice/sessoes',
  'pagamentos': '/backoffice/pagamentos',
  'planos': '/backoffice/planos',
  'relatorio-ia': '/backoffice/relatorio-ia',
  'pagamentos-consultores': '/backoffice/pagamentos-consultores',
  'carta-semana': '/backoffice/carta-semana',
  'banners': '/backoffice/banners',
  'integracoes': '/backoffice/integracoes',
  'emails': '/backoffice/emails',
  'config': '/backoffice/config',
  'suporte': '/backoffice/suporte'
};

export default function Sidebar({ activePage, isOpen, onClose }: SidebarProps) {
  const navigate = useNavigate();
  const [sidebarMetrics, setSidebarMetrics] = useState({
    totalConversationsToday: 0,
    estimatedAICost: 0,
    apiStatus: 'unknown' as 'ok' | 'error' | 'unknown',
  });

  useEffect(() => {
    const loadMetrics = async () => {
      try {
        const response = await fetchWithAdminAuth('/api/admin/getDashboardMetrics', { method: 'GET' });
        if (response.ok) {
          const data = await response.json();
          setSidebarMetrics({
            totalConversationsToday: Number(data?.totalConversationsToday ?? 0),
            estimatedAICost: Number(data?.estimatedAICost ?? 0),
            apiStatus: 'ok',
          });
        } else {
          setSidebarMetrics(prev => ({ ...prev, apiStatus: 'error' }));
        }
      } catch {
        setSidebarMetrics(prev => ({ ...prev, apiStatus: 'error' }));
      }
    };

    loadMetrics();
    // Refresh every 60s
    const interval = setInterval(loadMetrics, 60000);
    return () => clearInterval(interval);
  }, []);

  const handlePageChange = (page: PageType) => {
    navigate(PAGE_TO_ROUTE[page]);
    onClose(); // Close sidebar on mobile after navigation
  };

  const formatCurrency = (v: number) =>
    new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v);

  return (
    <>
      {/* Mobile overlay backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-screen w-[280px]
          bg-gradient-to-b from-[#05060a] via-[#050609] to-[#050508]
          border-r border-border-soft px-4 py-5 flex flex-col gap-6
          transition-transform duration-300 ease-in-out overflow-y-auto
          lg:sticky lg:translate-x-0 lg:w-[260px]
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Header with close button on mobile */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 px-3 py-2 rounded-full bg-[radial-gradient(circle_at_top_left,#262d46_0,transparent_60%)]">
            <div className="w-[34px] h-[34px] rounded-full bg-[conic-gradient(from_220deg,#e2b714,#e86fde,#4da9ff,#e2b714)] p-[2px]">
              <div className="w-full h-full rounded-full bg-[#05060a] flex items-center justify-center text-[18px] font-bold tracking-wide">
                TV
              </div>
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-[0.95rem] font-semibold">Tarot Virtual</span>
              <span className="text-[0.75rem] text-text-soft">Backoffice</span>
            </div>
          </div>
          {/* Close button - only visible on mobile */}
          <button
            onClick={onClose}
            className="lg:hidden w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/5 transition-colors text-text-soft hover:text-text"
          >
            ✕
          </button>
        </div>

      <div>
        <div className="text-[0.75rem] uppercase tracking-wider text-text-soft mb-1 px-2.5">Visão geral</div>
        <nav className="flex flex-col gap-1">
          <NavItem 
            icon="⌂" 
            label="Dashboard" 
            badge="Hoje"
            active={activePage === 'dashboard'}
            onClick={() => handlePageChange('dashboard')}
          />
        </nav>
      </div>

      <div>
        <div className="text-[0.75rem] uppercase tracking-wider text-text-soft mb-1 px-2.5">Operação</div>
        <nav className="flex flex-col gap-1">
          <NavItem icon="★" label="Consultores" active={activePage === 'consultores'} onClick={() => handlePageChange('consultores')} />
          <NavItem icon="☺" label="Clientes" active={activePage === 'clientes'} onClick={() => handlePageChange('clientes')} />
          <NavItem icon="✉" label="Sessões & Chat" active={activePage === 'sessoes'} onClick={() => handlePageChange('sessoes')} />
        </nav>
      </div>

      <div>
        <div className="text-[0.75rem] uppercase tracking-wider text-text-soft mb-1 px-2.5">Finanças</div>
        <nav className="flex flex-col gap-1">
          <NavItem icon="€" label="Pagamentos" active={activePage === 'pagamentos'} onClick={() => handlePageChange('pagamentos')} />
          <NavItem icon="⌗" label="Planos & Cupões" active={activePage === 'planos'} onClick={() => handlePageChange('planos')} />
          <NavItem icon="AI" label="Relatório IA / ChatGPT" active={activePage === 'relatorio-ia'} onClick={() => handlePageChange('relatorio-ia')} />
          <NavItem icon="⇄" label="Pagamentos a consultores" badge="Futuro" active={activePage === 'pagamentos-consultores'} onClick={() => handlePageChange('pagamentos-consultores')} />
        </nav>
      </div>

      <div>
        <div className="text-[0.75rem] uppercase tracking-wider text-text-soft mb-1 px-2.5">Conteúdo & Sistema</div>
        <nav className="flex flex-col gap-1">
          <NavItem icon="♧" label="Carta da Semana" active={activePage === 'carta-semana'} onClick={() => handlePageChange('carta-semana')} />
          <NavItem icon="▣" label="Banners & Textos" active={activePage === 'banners'} onClick={() => handlePageChange('banners')} />
          <NavItem icon="∞" label="Integrações & Tracking" active={activePage === 'integracoes'} onClick={() => handlePageChange('integracoes')} />
          <NavItem icon="@" label="Templates de Email" active={activePage === 'emails'} onClick={() => handlePageChange('emails')} />
          <NavItem icon="☑" label="Configurações" active={activePage === 'config'} onClick={() => handlePageChange('config')} />
          <NavItem icon="?" label="Suporte & Logs" active={activePage === 'suporte'} onClick={() => handlePageChange('suporte')} />
        </nav>
      </div>

      <div className="mt-auto pt-3 border-t border-border-soft flex flex-col gap-2.5">
        <div className="text-[0.75rem] text-text-soft leading-relaxed">
          Consultas IA hoje: <strong className="text-accent">{sidebarMetrics.totalConversationsToday}</strong><br />
          Custo IA estimado: <strong className="text-accent">{formatCurrency(sidebarMetrics.estimatedAICost)}</strong>
        </div>
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-border-soft text-[0.75rem] cursor-pointer hover:bg-white/5 transition-colors">
          <span className={`w-2 h-2 rounded-full ${
            sidebarMetrics.apiStatus === 'ok' 
              ? 'bg-success shadow-[0_0_0_4px_rgba(33,193,151,0.25)]' 
              : sidebarMetrics.apiStatus === 'error'
                ? 'bg-danger shadow-[0_0_0_4px_rgba(239,68,68,0.25)]'
                : 'bg-yellow-400 shadow-[0_0_0_4px_rgba(250,204,21,0.25)]'
          }`} />
          <span>{
            sidebarMetrics.apiStatus === 'ok' ? 'API ChatGPT operacional' 
            : sidebarMetrics.apiStatus === 'error' ? 'API indisponível'
            : 'A verificar...'
          }</span>
        </div>
      </div>
    </aside>
    </>
  );
}

interface NavItemProps {
  icon: string;
  label: string;
  badge?: string;
  active: boolean;
  onClick: () => void;
}

function NavItem({ icon, label, badge, active, onClick }: NavItemProps) {
  return (
    <div
      onClick={onClick}
      className={`
        flex items-center gap-2.5 px-3 py-2 rounded-xl cursor-pointer text-[0.87rem] border transition-all duration-150
        ${active 
          ? 'bg-[radial-gradient(circle_at_top_left,rgba(226,183,20,0.25),transparent_55%)] border-accent text-text' 
          : 'border-transparent text-text-soft hover:bg-white/[0.02] hover:border-border-soft hover:text-text hover:-translate-y-px'
        }
      `}
    >
      <div className="w-[22px] h-[22px] rounded-full border border-white/[0.18] flex items-center justify-center text-[0.78rem] opacity-90">
        {icon}
      </div>
      <div className="flex-1">{label}</div>
      {badge && (
        <span className="text-[0.7rem] px-2 py-0.5 rounded-full bg-white/[0.06]">
          {badge}
        </span>
      )}
    </div>
  );
}
